// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { useNavigate } from "react-router-dom";

import { useFormikContext } from "formik";
import PropTypes from "prop-types";

import { Button } from "components/elements";
import { TabIndicator } from "components/form";
import { trans } from "src/translations";

import styles from "./FormPagination.scss";

/**
 * @typedef {Object} FormPaginationProps
 * @property {number} activeTab - Index of active tab
 * @property {number} tabCount - Total number of tabs
 * @property {string} tabTitle - Title of the current tab
 * @property {boolean} [discarding] - Whether the discard operation is in progress
 * @property {string} [discardLabel] - Custom label for the discard button
 * @property {string} [submitLabel] - Custom label for the discard button
 * @property {boolean} [hasDraft=false] - Indicates whether form has draft, which means it can be not dirty, but yet we want to allow user to submit the form
 * @property {boolean} [saving] - Whether the save operation is in progress
 * @property {boolean} [validatingDraft] - Whether draft validation is in progress
 * @property {Function} [onDiscard] - Discard handler passed when draft form exists. Will discard saved draft state of the form
 * @property {Function} [onNext] - Function will return pathname for the next page
 * @property {Function} [onPrev] - Function will return pathname for the previous page
 * @property {Function} [onSave] - Save handler. Will save draft state of the form
 * @property {Function} [onSubmit] - Submit handler. Trigger a form submission
 * @property {string} [dataTestId="pagination"] - Test id for cypress
 */

/**
 * FormPagination component that provides navigation and action buttons for multi-step forms
 *
 * This component can handle use cases as follow :
 *
 *   - variant 1 : onSubmit & onSave function are being passed then component will show draftButtons and Submit on the last page
 *   - variant 2 : onSubmit & !onSave then component should not render draftButtons and show Submit on the last page ( test app form or structure form )
 *   - variant 3 : !onSubmit & onSave then component will render Save on the last page
 *
 *  Another example for variant 1, discard button will be disabled when onDiscard callback is not being passed.
 *  Parent component decides whether to pass callback depending on that the app has draft or not ( see edit snap app or app submission forms )
 *
 * @param {FormPaginationProps} props - The component props
 * @returns {JSX.Element} The rendered form pagination component
 */
export const FormPagination = ({
  activeTab,
  discarding,
  discardLabel = trans.APP_EDIT__DRAFT_DISCARD_BUTTON(),
  submitLabel = trans.SUBMIT(),
  hasDraft = false,
  saving,
  validatingDraft,
  onDiscard,
  onNext,
  onPrev,
  onSave,
  onSubmit,
  tabCount,
  tabTitle,
  dataTestId = "pagination",
}) => {
  const { dirty, isSubmitting } = useFormikContext();
  const navigate = useNavigate();
  const goTo = (pathname) => {
    navigate(pathname);
  };
  const disabled = isSubmitting || saving || discarding || validatingDraft;
  const isSaveDraftAsMainAction =
    typeof onSubmit !== "function" && typeof onSave === "function";
  const isLastTab = activeTab === tabCount - 1;

  const getActionButtonConfig = () => {
    switch (true) {
      case !isLastTab && Boolean(onNext):
        return {
          dataTestId: "pagination-next",
          children: trans.NEXT(),
          disabled: disabled,
          onClick: () => goTo(onNext()),
        };
      case isSaveDraftAsMainAction:
        return {
          dataTestId: `${dataTestId}-submit-button`,
          children: isSubmitting ? trans.SAVING() : trans.SAVE(),
          disabled: disabled,
          onClick: onSave,
        };
      default:
        return {
          dataTestId: `${dataTestId}-submit-button`,
          children: isSubmitting ? trans.SUBMITTING() : submitLabel,
          disabled: disabled || (!dirty && !hasDraft),
          onClick: onSubmit,
        };
    }
  };

  return (
    <div className={styles.pagination}>
      <div>
        {Boolean(onPrev) && activeTab !== 0 ? (
          <Button
            className={styles.btnPrevious}
            type="wizardNormal"
            onClick={() => goTo(onPrev())}
            size="large"
            disabled={disabled}
            dataTestId="pagination-previous"
          >
            {trans.PREV()}
          </Button>
        ) : null}
      </div>
      <div className={styles.tabName}>{tabTitle}</div>
      <TabIndicator tabsCount={tabCount} activeTab={activeTab} />
      <div className={styles.draftButtonsContainer}>
        {onSave && !isSaveDraftAsMainAction && (
          <Button
            type="normal"
            disabled={disabled || !dirty}
            processing={saving}
            onClick={onSave}
            dataTestId={`${dataTestId}-save-draft-button`}
          >
            {saving ? trans.SAVING() : trans.APP_EDIT__DRAFT_SAVE_BUTTON()}
          </Button>
        )}
        {onDiscard && (
          <Button
            type="normal"
            onClick={onDiscard}
            disabled={!onDiscard || disabled || (!dirty && !hasDraft)}
            processing={discarding}
            dataTestId={`${dataTestId}-discard-draft-button`}
          >
            {discarding ? trans.DISCARDING() : discardLabel}
          </Button>
        )}
      </div>
      <div>
        <Button type="wizardNormal" size="large" {...getActionButtonConfig()} />
      </div>
    </div>
  );
};

FormPagination.propTypes = {
  activeTab: PropTypes.number.isRequired,
  tabCount: PropTypes.number.isRequired,
  tabTitle: PropTypes.string.isRequired,
  discarding: PropTypes.bool,
  discardLabel: PropTypes.string,
  submitLabel: PropTypes.string,
  hasDraft: PropTypes.bool,
  saving: PropTypes.bool,
  validatingDraft: PropTypes.bool,
  onDiscard: PropTypes.func,
  onNext: PropTypes.func,
  onPrev: PropTypes.func,
  onSave: PropTypes.func,
  onSubmit: PropTypes.func,
  dataTestId: PropTypes.string,
};
